import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Renderer2,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit,
} from '@angular/core';
import { merge, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { Project, ProjectPagination } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { UserService } from 'app/layout/common/user/user.service';
import { AccountService } from 'app/core';
import { Account, FieldDef } from 'app/core/models/account.types';
import { ProjectTenantsService } from '../tenants.service';
import { Property } from 'app/modules/projects/projects.types';
import { ProjectTenantsAddUnitComponent } from 'app/modules/projects/details/tenants/modals/add-unit/add-unit.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PropertyTenant, PropertyUnit } from 'app/modules/projects/details/tenants/tenants.types';
import {
  MatLegacyTable as MatTable,
  MatLegacyTableDataSource as MatTableDataSource,
} from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Data } from '@angular/router';
import { ProjectTenantsComponent } from '../tenants.component';
import { MatDrawer } from '@angular/material/sidenav';
import { UntypedFormControl } from '@angular/forms';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';

@Component({
  selector: 'project-tenants-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectTenantsListComponent implements OnInit, AfterViewInit, OnDestroy {
  project: Project;
  account: Account;
  public property: Property;

  private _unsubscribeAll: Subject<void>;

  @ViewChild('matDrawer', { static: true })
  matDrawer: MatDrawer;

  drawerMode: 'over' | 'side';
  drawerOpened: boolean;
  searchInputControl: UntypedFormControl;
  tenantTableColumns: any[];
  propertyUnits: PropertyUnit[];
  propertyUnits$: Observable<PropertyUnit[]>;
  propertyUnitsCount: number = 0;
  unitNumberFieldDef: any;
  unitSizeFieldDef: any;
  unitFloorFieldDef: any;
  tenantNameFieldDef: any;
  tenantLeaseStartDateFieldDef: any;
  tenantLeaseEndDateFieldDef: any;
  tenantOccupancyEndDateFieldDef: any;
  tenantBaseMonthlyRentFieldDef: any;
  tenantPercentageRentFieldDef: any;
  tenantSalesFieldDef: any;
  pricePerSqFtDef: any;
  data: any;

  isLoading: boolean = false;

  propertyUnitsDataSource: MatTableDataSource<any>;
  @ViewChild(MatSort) sort: MatSort;

  /**
   * Constructor
   */
  constructor(
    public projectTenantsComponent: ProjectTenantsComponent,
    private _projectsService: ProjectsService,
    private _projectTenantsService: ProjectTenantsService,
    private _userService: UserService,
    private _accountService: AccountService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _matDialog: MatDialog,
    private _activatedRoute: ActivatedRoute,
    private confirmService: ConfirmService,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
    });
    this.searchInputControl = new UntypedFormControl();
    this.propertyUnitsDataSource = new MatTableDataSource();

    this.tenantTableColumns = [
      'unit_number',
      'unit_size',
      'floor',
      'name',
      'lease_start_date',
      'lease_end_date',
      'occupancy_end_date',
      'base_monthly_rent',
      'percentage_rent',
      'sales',
      'gross_rent',
      'price_per_sq_ft',
      'delete',
    ];

    this.unitNumberFieldDef = {
      name: 'unit_number',
      label: '',
      type: 'text',
    };

    this.unitSizeFieldDef = {
      name: 'unit_size',
      label: '',
      type: 'number',
    };

    this.unitFloorFieldDef = {
      name: 'floor',
      label: '',
      type: 'number',
    };

    this.tenantNameFieldDef = {
      name: 'name',
      label: '',
      type: 'text',
    };

    this.tenantLeaseStartDateFieldDef = {
      name: 'lease_start_date',
      label: '',
      type: 'datetime',
    };

    this.tenantLeaseEndDateFieldDef = {
      name: 'lease_end_date',
      label: '',
      type: 'datetime',
    };

    this.tenantOccupancyEndDateFieldDef = {
      name: 'occupancy_end_date',
      label: '',
      type: 'datetime',
    };

    this.tenantBaseMonthlyRentFieldDef = {
      name: 'base_monthly_rent',
      label: '',
      type: 'money',
    };

    this.tenantPercentageRentFieldDef = {
      name: 'percentage_rent',
      label: '',
      type: 'percent',
    };

    this.tenantPercentageRentFieldDef = {
      name: 'percentage_rent',
      label: '',
      type: 'percent',
    };

    this.tenantSalesFieldDef = {
      name: 'sales',
      label: '',
      type: 'money',
    };

    this.pricePerSqFtDef = {
      name: 'price_per_sq_ft',
      label: '',
      type: 'money',
    };
  }

  ngOnInit(): void {
    this._projectTenantsService.property$.pipe(takeUntil(this._unsubscribeAll)).subscribe((property: Property) => {
      this.property = property;
      this.loadPropertyUnits();
    });

    this.propertyUnits$ = this._projectTenantsService.propertyUnits$;
    this._projectTenantsService.propertyUnits$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((propertyUnits: PropertyUnit[]) => {
        if (propertyUnits) {
          this.propertyUnits = propertyUnits;
          this.propertyUnitsCount = this.propertyUnits.length;
          this.propertyUnitsDataSource = new MatTableDataSource();
          this.propertyUnitsDataSource.data = this.propertyUnits;
        }

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    this.searchInputControl.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(500)).subscribe((query) => {
      this.propertyUnitsDataSource.filter = query.trim().toLowerCase();
    });
  }

  loadPropertyUnits(sort?): void {
    this._projectTenantsService.getPropertyUnits(this.property.id, sort).subscribe();
  }

  onUpdatePropertyUnit($event, propertyUnit) {
    let pu = Object.assign(propertyUnit, $event);
    this._projectTenantsService.updatePropertyUnit(pu).subscribe();
  }

  onUpdatePropertyTenant($event, propertyUnit, propertyTenant) {
    let pt = Object.assign(propertyTenant, $event);
    this._projectTenantsService.updatePropertyTenant(propertyUnit.id, pt).subscribe();
  }

  deletePropertyUnit(propertyUnit) {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this._projectTenantsService.deletePropertyUnit(propertyUnit.id).subscribe();
      });
  }

  /**
   * Create contact
   */
  addUnit(): void {
    // Create the contact
    /*this._contactsService.createContact({first_name: 'New', last_name: 'Contact'}).subscribe((newContact) => {

            // Go to new contact
            this.goToContact(newContact.id);
        });*/
    // Open the dialog
    const dialogRef = this._matDialog.open(ProjectTenantsAddUnitComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { property: this.property },
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  ngAfterViewInit(): void {
    this.propertyUnitsDataSource.sort = this.sort;
    this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
      this.loadPropertyUnits({
        sort: sort.active,
        order: sort.direction,
      });
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
